import Echo from 'laravel-echo';
import Pusher from 'pusher-js';
import { getCsrfToken } from './lib/csrf';
import { logger } from './lib/utils';

// Declare Echo on window for global access
declare global {
    interface Window {
        Echo: Echo<any>; // eslint-disable-line @typescript-eslint/no-explicit-any
        Pusher: typeof Pusher;
    }
}

// Configure Pusher-compatible client for Soketi
// Note: Soketi is Pusher-protocol compatible, so we use the pusher-js library
window.Pusher = Pusher;

const envHost = import.meta.env.VITE_PUSHER_HOST;
const envScheme = import.meta.env.VITE_PUSHER_SCHEME;
const envPort = import.meta.env.VITE_PUSHER_PORT;

// Determine if we're in production (HTTPS) or development
const isProduction = window.location.protocol === 'https:';
const isSecure = isProduction || envScheme === 'https';

// For production (HTTPS), use the same hostname and port 443 (or default port)
// The WebSocket connection will go through the same domain, which should be proxied to Soketi
// For development, use the configured host/port
let derivedHost: string;
let resolvedPort: number;

if (isProduction) {
    // In production, always use the same hostname as the page
    // This allows the reverse proxy (Traefik/Nginx) to handle WebSocket connections
    derivedHost = window.location.hostname;
    // Use port 443 for WSS or the configured port if provided
    resolvedPort = envPort ? Number(envPort) : 443;
} else {
    // In development, use the configured host or default to localhost
    derivedHost = !envHost || envHost === 'soketi' ? window.location.hostname : envHost;
    resolvedPort = envPort ? Number(envPort) : 6001;
}

// Initialize Laravel Echo
window.Echo = new Echo({
    broadcaster: 'pusher',
    key: import.meta.env.VITE_PUSHER_APP_KEY,
    cluster: import.meta.env.VITE_PUSHER_APP_CLUSTER,
    wsHost: derivedHost || 'localhost',
    wsPort: resolvedPort,
    wssPort: resolvedPort,
    forceTLS: isSecure,
    enabledTransports: ['ws', 'wss'],
    authEndpoint: '/broadcasting/auth',
    auth: {
        headers: {
            'X-CSRF-TOKEN': getCsrfToken(),
            Accept: 'application/json',
        },
    },
    withCredentials: true,
});

// Add debugging for WebSocket connection (Soketi)
// Note: WebSocket errors are non-critical and won't prevent form submission
window.Echo.connector.pusher.connection.bind('error', (error: Error) => {
    // Only log if it's not a standard WebSocket connection error (Soketi might not be running)
    if (error && typeof error === 'object' && 'error' in error) {
        const wsError = (error as { error?: { type?: string } }).error;
        if (wsError?.type !== 'WebSocketError') {
            logger.warn('WebSocket connection issue (non-critical, real-time features may be unavailable):', error);
        }
    } else {
        logger.warn('WebSocket connection issue (non-critical, real-time features may be unavailable):', error);
    }
});

window.Echo.connector.pusher.connection.bind('disconnected', () => {
    logger.log('WebSocket disconnected');
});

window.Echo.connector.pusher.connection.bind('connected', () => {
    logger.log('WebSocket connected');
});

logger.log('Echo initialized with config:', {
    key: import.meta.env.VITE_PUSHER_APP_KEY,
    cluster: import.meta.env.VITE_PUSHER_APP_CLUSTER,
    wsHost: derivedHost,
    wsPort: resolvedPort,
    isSecure,
    isProduction,
    authEndpoint: '/broadcasting/auth',
    csrfToken: getCsrfToken(),
});

export default window.Echo;
